import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, ChevronRight } from "lucide-react";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import WhatsappFloat from "@/components/site/WhatsappFloat";
import QuoteDialog from "@/components/site/QuoteDialog";
import SEO from "@/components/seo/SEO";
import { getPostBySlug, BLOG_CATEGORIES, BLOG_POSTS } from "@/data/blog";

const META_ICON_SIZE_PX = 13;

// Static framer-motion configs — module-level for stable referential identity.
const COVER_INITIAL = { opacity: 0, scale: 0.98 };
const COVER_ANIMATE = { opacity: 1, scale: 1 };
const COVER_TRANSITION = { duration: 0.7 };

const renderParagraph = (text, paragraphIndex) => {
  // simple markdown-ish bold (**foo**)
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <p key={`p-${paragraphIndex}`} className="mt-5 text-[var(--mn-black)] leading-[1.75] text-[1.05rem]">
      {parts.map((part, j) => {
        const partKey = `p-${paragraphIndex}-${j}-${part.slice(0, 12)}`;
        if (part.startsWith("**") && part.endsWith("**")) {
          return (
            <strong key={partKey} className="text-[var(--mn-black)] font-bold">
              {part.slice(2, -2)}
            </strong>
          );
        }
        return <span key={partKey}>{part}</span>;
      })}
    </p>
  );
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = getPostBySlug(slug);
  const [open, setOpen] = useState(false);

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-5 text-center">
        <h1 className="font-serif-display font-extrabold text-3xl text-[var(--mn-black)]">Artigo não encontrado</h1>
        <button onClick={() => navigate("/blog")} className="btn-mn-primary mt-7">Ir para o blog</button>
      </div>
    );
  }

  const cat = BLOG_CATEGORIES.find((c) => c.slug === post.category);
  const related = BLOG_POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    image: post.cover,
    datePublished: post.date,
    dateModified: post.date,
    author: { "@type": "Person", name: post.author },
    publisher: { "@type": "Organization", name: "M NAIEF Brindes Personalizados", logo: { "@type": "ImageObject", url: "https://mnaief.com.br/logo.png" } },
    mainEntityOfPage: { "@type": "WebPage", "@id": `https://mnaief.com.br/blog/${post.slug}` },
    keywords: post.keywords,
  };

  return (
    <div className="App" data-testid="blog-post-page">
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={post.keywords}
        image={post.cover}
        url={`/blog/${post.slug}`}
        type="article"
        jsonLd={jsonLd}
      />
      <Header onQuoteClick={() => setOpen(true)} />

      <main className="pt-[120px] lg:pt-[180px] pb-16 bg-white">
        <article className="mn-container">
          <nav className="text-xs uppercase tracking-[0.2em] text-[var(--mn-dark)] flex items-center gap-2 mb-6" aria-label="breadcrumb" data-testid="post-breadcrumb">
            <Link to="/" className="hover:text-[var(--mn-gold)]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/blog" className="hover:text-[var(--mn-gold)]">Blog</Link>
            <ChevronRight size={12} />
            <span className="text-[var(--mn-gold)] line-clamp-1 max-w-[60vw]">{post.title}</span>
          </nav>

          <div className="max-w-3xl">
            <span className="text-[0.7rem] uppercase tracking-[0.24em] font-semibold text-[var(--mn-gold)]">{cat?.name || "Geral"}</span>
            <h1 className="mn-h-display text-[2.25rem] sm:text-[2.75rem] lg:text-[3.2rem] mt-3 leading-[1.05]" data-testid="post-title">
              {post.title}
            </h1>
            <div className="mt-5 flex flex-wrap items-center gap-5 text-xs text-[var(--mn-dark)]">
              <span className="inline-flex items-center gap-1.5"><Calendar size={META_ICON_SIZE_PX} /> {new Date(post.date).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}</span>
              <span className="inline-flex items-center gap-1.5"><Clock size={META_ICON_SIZE_PX} /> {post.readTime}</span>
              <span>Por <strong className="text-[var(--mn-black)]">{post.author}</strong></span>
            </div>
          </div>

          <motion.div
            initial={COVER_INITIAL}
            animate={COVER_ANIMATE}
            transition={COVER_TRANSITION}
            className="mt-10 aspect-[16/8] max-w-[1100px] overflow-hidden rounded-[4px] bg-[#0e0e0e]"
          >
            <img src={post.cover} alt={post.title} className="w-full h-full object-cover" loading="eager" />
          </motion.div>

          <div className="mt-10 max-w-3xl" data-testid="post-body">
            <p className="text-lg text-[var(--mn-dark)] font-serif-display leading-relaxed italic border-l-2 border-[var(--mn-gold)] pl-5">
              {post.excerpt}
            </p>
            {post.body.map(renderParagraph)}

            {/* CTA in-article */}
            <div className="mt-12 border border-[var(--mn-line)] rounded-[4px] p-7 bg-[var(--mn-cream)]" data-testid="post-cta">
              <span className="mn-eyebrow">Próximo passo</span>
              <h3 className="font-serif-display font-bold text-2xl text-[var(--mn-black)] mt-3 leading-tight">
                Pronto para a sua próxima ação? <span style={{ color: "var(--mn-gold)" }}>A gente cuida de tudo.</span>
              </h3>
              <p className="mt-3 text-sm text-[var(--mn-dark)] leading-relaxed">
                Solicite um orçamento sem compromisso e receba uma proposta em poucas horas.
              </p>
              <button onClick={() => setOpen(true)} className="btn-mn-primary mt-5" data-testid="post-cta-quote">
                Solicitar orçamento
              </button>
            </div>

            <Link to="/blog" className="inline-flex items-center gap-2 mt-10 text-xs uppercase tracking-[0.22em] font-medium text-[var(--mn-dark)] hover:text-[var(--mn-gold)]">
              <ArrowLeft size={14} /> Voltar para o blog
            </Link>
          </div>

          {/* Related */}
          <section className="mt-20 border-t border-[var(--mn-line)] pt-14">
            <span className="mn-eyebrow">Continue lendo</span>
            <h2 className="mn-h-display text-[1.75rem] mt-3">Artigos relacionados</h2>
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              {related.map((p) => {
                const c = BLOG_CATEGORIES.find((ct) => ct.slug === p.category);
                return (
                  <Link key={p.slug} to={`/blog/${p.slug}`} className="group block border border-[var(--mn-line)] rounded-[4px] overflow-hidden hover:border-[var(--mn-gold)] transition-colors">
                    <div
                      className="aspect-square overflow-hidden flex items-center justify-center"
                      style={{ background: "#F5F5F5", padding: "12px" }}
                    >
                      <img
                        src={p.cover}
                        alt={p.title}
                        loading="lazy"
                        className="max-w-full max-h-full w-auto h-auto object-contain"
                        style={{
                          maxWidth: "100%",
                          maxHeight: "100%",
                          transition: "transform 0.3s ease",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.12)")}
                        onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                      />
                    </div>
                    <div className="p-5">
                      <span className="text-[0.6rem] uppercase tracking-[0.22em] font-semibold text-[var(--mn-gold)]">{c?.name}</span>
                      <h3 className="font-serif-display font-bold text-base mt-2 text-[var(--mn-black)] group-hover:text-[var(--mn-gold)] line-clamp-2 leading-snug">
                        {p.title}
                      </h3>
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        </article>
      </main>

      <Footer />
      <WhatsappFloat />
      <QuoteDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}
